/**
 * TextRegistry — Static registry of simplified vs normal text variants.
 *
 * Each key maps to { normal, simple } text strings.
 * The `t(key)` helper in useTextMode returns the correct variant.
 *
 * Convention: keys use dot notation by domain.
 *
 * @since Text Simplification System
 */

export type TextMode = "simple" | "normal";

interface TextEntry {
  normal: string;
  simple: string;
}

const TEXT_REGISTRY: Record<string, TextEntry> = {
  // ── GenerationShell: Session ──
  "generation.session.explanation": {
    normal: "L'IA génère une séance complète (exercices, séries, repos) adaptée à ton profil et tes contraintes.",
    simple: "On crée ta séance sur mesure. Dis-nous ce que tu veux, on s'occupe du reste.",
  },
  "generation.session.advantage.1": {
    normal: "Adaptée à ton niveau et tes objectifs",
    simple: "Faite pour ton niveau",
  },
  "generation.session.advantage.2": {
    normal: "Exercices validés par des faits scientifiques",
    simple: "Exercices fiables et testés",
  },
  "generation.session.advantage.3": {
    normal: "Garde-fous automatiques (blessures, restrictions)",
    simple: "On évite ce qui pourrait te blesser",
  },
  "generation.session.risk.1": {
    normal: "Ajuste la durée si tu n'as pas le temps prévu",
    simple: "Adapte la durée si besoin",
  },
  "generation.session.risk.2": {
    normal: "Vérifie les charges — l'IA ne connaît pas tes max",
    simple: "Vérifie les poids avant de commencer",
  },
  "generation.session.tip.1": {
    normal: "Sois précis dans ta description pour un meilleur résultat",
    simple: "Plus tu décris, mieux c'est",
  },

  // ── GenerationShell: Program ──
  "generation.program.explanation": {
    normal: "L'IA construit un programme structuré (entraînement + nutrition + prévention) sur plusieurs semaines.",
    simple: "On crée ton plan d'entraînement complet sur plusieurs semaines.",
  },
  "generation.program.advantage.1": {
    normal: "Planification complète multi-semaines",
    simple: "Plan sur plusieurs semaines",
  },
  "generation.program.advantage.2": {
    normal: "Nutrition et prévention incluses",
    simple: "Conseils nutrition et prévention inclus",
  },
  "generation.program.advantage.3": {
    normal: "Basé sur des faits scientifiques validés",
    simple: "Basé sur des infos fiables",
  },
  "generation.program.advantage.4": {
    normal: "Traçabilité totale de chaque recommandation",
    simple: "Chaque conseil est justifié",
  },
  "generation.program.risk.1": {
    normal: "Programme générique si le profil est incomplet",
    simple: "Résultat moins précis si ton profil est vide",
  },
  "generation.program.risk.2": {
    normal: "Les durées courtes (<2 sem) limitent la progression",
    simple: "Choisis au moins 2 semaines pour de bons résultats",
  },
  "generation.program.tip.1": {
    normal: "Complete ton profil pour un programme plus précis",
    simple: "Remplis ton profil pour un meilleur résultat",
  },

  // ── GenerationShell: Common ──
  "generation.safety.label": {
    normal: "Garde-fous actifs. Blessures, restrictions et exercices évités sont pris en compte.",
    simple: "On fait attention à tes blessures et limites.",
  },
  "generation.profile.incomplete": {
    normal: "Plus ton profil est complet, plus le résultat sera précis.",
    simple: "Complète ton profil pour un meilleur résultat.",
  },
  "generation.profile.header": {
    normal: "Pris en compte",
    simple: "On utilise",
  },
  "generation.tip.label": {
    normal: "Conseil",
    simple: "Astuce",
  },

  // ── NextStepCard ──
  "nextstep.title": {
    normal: "Prochaine étape",
    simple: "Que faire ?",
  },
  "nextstep.next": {
    normal: "Ensuite",
    simple: "Après",
  },
  "nextstep.later": {
    normal: "Plus tard",
    simple: "Plus tard",
  },

  // ── HelpDrawer ──
  "help.drawer.title": {
    normal: "Aide contextuelle",
    simple: "Besoin d'aide ?",
  },
  "help.section.purpose": {
    normal: "Objectif",
    simple: "C'est quoi ?",
  },
  "help.section.howto": {
    normal: "Mode d'emploi",
    simple: "Comment faire ?",
  },
  "help.section.when": {
    normal: "Quand l'utiliser",
    simple: "Quand venir ici ?",
  },
  "help.section.tips": {
    normal: "Conseils",
    simple: "Astuces",
  },
  "help.section.example": {
    normal: "Exemple",
    simple: "Exemple",
  },
};

/**
 * Get a text entry for a given mode.
 * Falls back to normal if key doesn't exist or mode variant is missing.
 */
export function getText(key: string, mode: TextMode): string {
  const entry = TEXT_REGISTRY[key];
  if (!entry) return key;
  return entry[mode] ?? entry.normal ?? key;
}

/**
 * Check if a key exists in the registry.
 */
export function hasTextKey(key: string): boolean {
  return key in TEXT_REGISTRY;
}
